"use strict";
/**
 * Pricing System
 *
 * Replaces the old 23-code PricingRuleCode + MarketCompetition system
 * with two orthogonal fields: PricePolicy (how to price) and PricingTier (margin level).
 *
 * This enables formula-driven recommendedPrice calculation:
 *   recommendedPrice = unitCostBI * markupFactor * (1 + vatSale/100)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MIN_PRICE_COMP_SVC_SALE_FRACTION = exports.MIN_PRICE_EXT_COST_FACTOR = exports.MIN_PRICE_COST_FACTOR = exports.ALLOWED_POLICIES_BY_SECTION_ID = exports.COMP_SVC_SECTION_IDS = exports.NON_SELLABLE_SECTION_IDS = exports.PRICED_SECTION_IDS = exports.NO_FORMULA_POLICIES = exports.AUTO_TIER_POLICIES = exports.AUTO_TIER_RANGES = exports.MARKUP_FACTORS = exports.PRICING_TIER_VALUES = exports.PRICE_POLICY_VALUES = exports.PRICING_TIER_LABELS = exports.PRICE_POLICY_LABELS = exports.PricingTier = exports.PricePolicy = void 0;
exports.isPolicyAllowedForSection = isPolicyAllowedForSection;
exports.allowedPoliciesForSection = allowedPoliciesForSection;
exports.getAutoTier = getAutoTier;
exports.getMarkupFactor = getMarkupFactor;
exports.getDefaultPricePolicy = getDefaultPricePolicy;
exports.calculateRecommendedPricePVP = calculateRecommendedPricePVP;
exports.calculateFloorPrices = calculateFloorPrices;
const qvet_catalog_1 = require("./qvet-catalog");
// =============================================================================
// ENUMS
// =============================================================================
/**
 * PricePolicy - HOW the item is priced
 *
 * Each policy describes the pricing strategy for a category of items.
 */
var PricePolicy;
(function (PricePolicy) {
    /** Pharmacy unit sales (original presentation) */
    PricePolicy["PHARM"] = "PHARM";
    /** Pharmacy fractioned (sold in smaller quantities than purchased) */
    PricePolicy["PHARM_FRAC"] = "PHARM_FRAC";
    /** Per-ml sales (liquid pharmacy items sold by ml) */
    PricePolicy["COST_ML"] = "COST_ML";
    /** Medical supplies / other articles (cost-based) */
    PricePolicy["COST"] = "COST";
    /** External services (commission-based) */
    PricePolicy["EXT_COST"] = "EXT_COST";
    /** Internal services (priced by market study / competition) */
    PricePolicy["COMP_SVC"] = "COMP_SVC";
    /** Individual formula per item */
    PricePolicy["SPECIAL"] = "SPECIAL";
    /** Fixed manual price (no formula) */
    PricePolicy["MANUAL"] = "MANUAL";
    /** Not sold (internal use, equipment, etc.) */
    PricePolicy["NONE"] = "NONE";
})(PricePolicy || (exports.PricePolicy = PricePolicy = {}));
/**
 * PricingTier - Margin level within a policy
 */
var PricingTier;
(function (PricingTier) {
    PricingTier["LOW_MARGIN"] = "LOW_MARGIN";
    PricingTier["MID_MARGIN"] = "MID_MARGIN";
    PricingTier["HIGH_MARGIN"] = "HIGH_MARGIN";
})(PricingTier || (exports.PricingTier = PricingTier = {}));
// =============================================================================
// LABELS (Spanish, for UI)
// =============================================================================
exports.PRICE_POLICY_LABELS = {
    [PricePolicy.PHARM]: 'Farmacia (presentación original)',
    [PricePolicy.PHARM_FRAC]: 'Farmacia (fraccionado)',
    [PricePolicy.COST_ML]: 'Farmacia (por ml)',
    [PricePolicy.COST]: 'Insumos / Artículos (por costo)',
    [PricePolicy.EXT_COST]: 'Servicios externos',
    [PricePolicy.COMP_SVC]: 'Servicios internos (estudio de mercado)',
    [PricePolicy.SPECIAL]: 'Fórmula individual',
    [PricePolicy.MANUAL]: 'Precio manual',
    [PricePolicy.NONE]: 'No se vende',
};
exports.PRICING_TIER_LABELS = {
    [PricingTier.LOW_MARGIN]: 'Margen bajo',
    [PricingTier.MID_MARGIN]: 'Margen medio',
    [PricingTier.HIGH_MARGIN]: 'Margen alto',
};
exports.PRICE_POLICY_VALUES = Object.values(PricePolicy);
exports.PRICING_TIER_VALUES = Object.values(PricingTier);
// =============================================================================
// MARKUP TABLES
// =============================================================================
/**
 * Policies that have a formula (cost * factor).
 * Keys: PricePolicy, Values: { LOW_MARGIN, MID_MARGIN, HIGH_MARGIN } → factor
 */
/**
 * ⚠️ **No one has written down where these numbers come from.**
 *
 * Not the factors, not the cost thresholds in {@link AUTO_TIER_RANGES}, not the floor
 * multipliers. No source, no date, no "reviewed on". They were noted as unexplained on
 * 2026-09-11 and the note is deliberately left here rather than in a plan: this is the file
 * someone opens when they wonder whether 1.33 is still right.
 *
 * **That matters because they are load-bearing.** They set the recommended price of every
 * article the app prices automatically, and a number nobody can justify is a number nobody
 * dares to change — so it never gets revisited, which is the worst of both worlds.
 *
 * When they are next reviewed, record **for each row**: who set it, against what (a margin
 * target? a competitor? a supplier's list?), and when. Until then, treat them as inherited,
 * not as agreed.
 */
exports.MARKUP_FACTORS = {
    [PricePolicy.PHARM]: { [PricingTier.LOW_MARGIN]: 1.33, [PricingTier.MID_MARGIN]: 1.67, [PricingTier.HIGH_MARGIN]: 2.00 },
    [PricePolicy.PHARM_FRAC]: { [PricingTier.LOW_MARGIN]: 2.00, [PricingTier.MID_MARGIN]: 2.50, [PricingTier.HIGH_MARGIN]: 3.00 },
    [PricePolicy.COST_ML]: { [PricingTier.LOW_MARGIN]: 3.00, [PricingTier.MID_MARGIN]: 5.00, [PricingTier.HIGH_MARGIN]: 10.00 },
    [PricePolicy.COST]: { [PricingTier.LOW_MARGIN]: 2.00, [PricingTier.MID_MARGIN]: 3.00, [PricingTier.HIGH_MARGIN]: 5.00 },
    [PricePolicy.EXT_COST]: { [PricingTier.LOW_MARGIN]: 1.40, [PricingTier.MID_MARGIN]: 1.70, [PricingTier.HIGH_MARGIN]: 2.00 },
};
exports.AUTO_TIER_RANGES = {
    [PricePolicy.COST_ML]: { highMarginMax: 5, midMarginMax: 50 },
    [PricePolicy.COST]: { highMarginMax: 30, midMarginMax: 500 },
    [PricePolicy.EXT_COST]: { highMarginMax: 600, midMarginMax: 2000 },
};
/** Policies where tier is auto-calculated from cost */
exports.AUTO_TIER_POLICIES = [
    PricePolicy.COST_ML,
    PricePolicy.COST,
    PricePolicy.EXT_COST,
];
/** Policies that have no formula (recommendedPrice = null) */
exports.NO_FORMULA_POLICIES = [
    PricePolicy.COMP_SVC,
    PricePolicy.SPECIAL,
    PricePolicy.MANUAL,
    PricePolicy.NONE,
];
// =============================================================================
// SECTION → DEFAULT POLICY MAPPING
// =============================================================================
/**
 * 🚨 **These tables are keyed by SECTION ID, not by name — and that is the whole point.**
 *
 * They used to match on the section's **name** (`'FARMACIA'`, `'SERVICIOS MEDICOS'`…), and that
 * was not merely fragile: {@link getDefaultPricePolicy} runs on **every sync**, so the day QVET
 * renamed a section, every auto-priced item in it would fall through to `MANUAL` and have its
 * policy **rewritten in place**, silently. Measured on 2026-09-11: that is 369 pharmacy items,
 * 215 medical services, 193 supplies. QVET renaming things is not hypothetical — renaming a
 * tariff once took the whole catalog sync down.
 *
 * Ids come from {@link QVET_SECTION_IDS}, measured against the synced catalog.
 */
/**
 * The sections this file actually prices — QVET's ten **active** ones.
 *
 * An id outside this list is not a match: see {@link getDefaultPricePolicy}.
 */
exports.PRICED_SECTION_IDS = Object.values(qvet_catalog_1.QVET_SECTION_IDS);
/** Sections where items are not sold (pricePolicy = NONE) */
exports.NON_SELLABLE_SECTION_IDS = [
    qvet_catalog_1.QVET_SECTION_IDS.CONSUMO_INTERNO,
    qvet_catalog_1.QVET_SECTION_IDS.EQUIPAMIENTO,
    qvet_catalog_1.QVET_SECTION_IDS.INSUMOS_ESCANDALLO,
];
/**
 * Sections that use COMP_SVC policy (market-study pricing).
 * No formula — prices set by competitive analysis.
 */
exports.COMP_SVC_SECTION_IDS = [
    qvet_catalog_1.QVET_SECTION_IDS.SERVICIOS_MEDICOS,
    qvet_catalog_1.QVET_SECTION_IDS.OTROS_SERVICIOS,
];
/**
 * Allowed policies per section, for validation.
 *
 * ⚠️ **This table had zero readers until 2026-09-11.** It sat here looking like a rule and
 * enforced nothing — anyone could put a pharmacy markup on a medical service. It is now
 * enforced by {@link isPolicyAllowedForSection}, wired into the catalog item's policy setter.
 *
 * A section that is **not listed allows every policy**. That is deliberate, and it covers
 * exactly QVET's ten retired sections — HISTORICO among them, with 3.337 items, all `MANUAL`.
 * Nothing new is created in them, so constraining them would only block corrections.
 */
exports.ALLOWED_POLICIES_BY_SECTION_ID = {
    [qvet_catalog_1.QVET_SECTION_IDS.CONSUMO_INTERNO]: [PricePolicy.NONE],
    [qvet_catalog_1.QVET_SECTION_IDS.EQUIPAMIENTO]: [PricePolicy.NONE],
    [qvet_catalog_1.QVET_SECTION_IDS.INSUMOS_ESCANDALLO]: [PricePolicy.NONE],
    [qvet_catalog_1.QVET_SECTION_IDS.FARMACIA]: [PricePolicy.PHARM, PricePolicy.PHARM_FRAC, PricePolicy.COST_ML, PricePolicy.SPECIAL, PricePolicy.MANUAL],
    [qvet_catalog_1.QVET_SECTION_IDS.FARMACIA_INTERNA]: [PricePolicy.PHARM, PricePolicy.PHARM_FRAC, PricePolicy.COST_ML, PricePolicy.SPECIAL, PricePolicy.MANUAL],
    [qvet_catalog_1.QVET_SECTION_IDS.INSUMOS_MEDICOS]: [PricePolicy.COST, PricePolicy.SPECIAL, PricePolicy.MANUAL],
    [qvet_catalog_1.QVET_SECTION_IDS.OTROS_ARTICULOS]: [PricePolicy.COST, PricePolicy.SPECIAL, PricePolicy.MANUAL],
    [qvet_catalog_1.QVET_SECTION_IDS.SERVICIOS_MEDICOS]: [PricePolicy.COMP_SVC, PricePolicy.SPECIAL, PricePolicy.MANUAL],
    [qvet_catalog_1.QVET_SECTION_IDS.OTROS_SERVICIOS]: [PricePolicy.COMP_SVC, PricePolicy.SPECIAL, PricePolicy.MANUAL],
    [qvet_catalog_1.QVET_SECTION_IDS.SERVICIOS_EXTERNOS]: [PricePolicy.EXT_COST, PricePolicy.SPECIAL, PricePolicy.MANUAL],
};
/**
 * Is this policy allowed for this section?
 *
 * An unknown or unlisted section allows everything — see the note on
 * {@link ALLOWED_POLICIES_BY_SECTION_ID}. Never guess a section id to make this stricter: a
 * wrong id here rejects a legitimate edit.
 */
function isPolicyAllowedForSection(sectionId, policy) {
    if (sectionId === undefined)
        return true;
    const allowed = exports.ALLOWED_POLICIES_BY_SECTION_ID[sectionId];
    return allowed === undefined || allowed.includes(policy);
}
/** The policies a section accepts, or `null` when it accepts all of them. */
function allowedPoliciesForSection(sectionId) {
    if (sectionId === undefined)
        return null;
    return exports.ALLOWED_POLICIES_BY_SECTION_ID[sectionId] ?? null;
}
// =============================================================================
// HELPER FUNCTIONS
// =============================================================================
/**
 * Get the auto-calculated tier based on unit cost.
 *
 * For auto-tier policies (COST_ML, COST, EXT_COST), the tier is determined
 * by the unit cost. Cheaper items get higher margins because customers
 * are less price-sensitive on low-cost items.
 *
 * @returns PricingTier, or null if the policy doesn't support auto-tier
 */
function getAutoTier(policy, unitCostBI) {
    const range = exports.AUTO_TIER_RANGES[policy];
    if (!range)
        return null;
    if (unitCostBI <= range.highMarginMax)
        return PricingTier.HIGH_MARGIN;
    if (unitCostBI <= range.midMarginMax)
        return PricingTier.MID_MARGIN;
    return PricingTier.LOW_MARGIN;
}
/**
 * Get the markup factor for a policy + tier combination.
 *
 * @returns The factor to multiply unitCostBI by, or null if no formula exists
 */
function getMarkupFactor(policy, tier) {
    const tierFactors = exports.MARKUP_FACTORS[policy];
    if (!tierFactors)
        return null;
    return tierFactors[tier] ?? null;
}
/**
 * Determine the default PricePolicy (and optionally PricingTier) for an item from its section,
 * sale unit and conversion factor.
 *
 * Used when creating an item from the QVET sync, when migrating existing ones, and on **every
 * subsequent sync** to keep an auto-assigned policy in step with the item's section.
 *
 * ## `matchedSection` is the important part of the answer
 *
 * 🚨 **This function runs on every sync and rewrites the stored policy.** So "I don't recognise
 * this section" and "this section means MANUAL" must not look the same to the caller — if they
 * do, a renamed or unread section quietly re-prices everything in it. They used to look the
 * same: both returned `MANUAL`.
 *
 * Now an unrecognised section comes back with `matchedSection: false`, and the caller's job is
 * to **leave the stored policy alone**. See `CatalogItem.applySyncFromQvet`.
 *
 * @param sectionId  QVET's `Seccio.Id`, from the item's ficha. The **stable** key: it survives
 *                   a rename. Used when the name resolves to nothing.
 * @param sectionName  the name the reports carry, on every sync. The **fresh** key: it is what
 *                     notices that an item was moved to another section. Tried first.
 */
function getDefaultPricePolicy(sectionId, sectionName, saleUnit, conversionFactor) {
    // ⚠️ **The NAME is tried first, and that is not a slip.** The two keys are not "reliable" and
    // "unreliable" — they are *fresh* and *stable*, and each fixes what the other cannot:
    //
    //   · the name comes from the report, on **every sync**, so it is what notices that an item
    //     was **moved to another section** — the case the old code handled correctly;
    //   · the id comes from the item's ficha, which is read rarely, so it goes stale on a move —
    //     but it is what survives QVET **renaming** a section, which is the case that used to
    //     silently re-price everything in it.
    //
    // Name first, id as the fallback, and both are allowed to fail: that is the only order that
    // gets a move AND a rename right.
    const candidato = qvet_catalog_1.QVET_SECTION_ID_BY_NAME[sectionName] ?? sectionId;
    // ⚠️ An id we don't know is NOT a match. Trusting any number here was the first version's
    // bug: a retired section's id (HISTORICO is 2) would have counted as "recognised" and
    // authorised overwriting the stored policy — the exact thing this guard exists to stop.
    const id = candidato !== undefined && exports.PRICED_SECTION_IDS.includes(candidato) ? candidato : undefined;
    if (id === undefined) {
        // Neither key resolved to a section we price. Not an error — QVET's ten retired sections
        // live here, HISTORICO among them — but it IS the case where nothing may be overwritten.
        return { policy: PricePolicy.MANUAL, tier: null, matchedSection: false };
    }
    // Non-sellable sections
    if (exports.NON_SELLABLE_SECTION_IDS.includes(id)) {
        return { policy: PricePolicy.NONE, tier: null, matchedSection: true };
    }
    // Services (market study pricing, no formula)
    if (exports.COMP_SVC_SECTION_IDS.includes(id)) {
        return { policy: PricePolicy.COMP_SVC, tier: null, matchedSection: true };
    }
    // External services (cost-based with auto-tier)
    if (id === qvet_catalog_1.QVET_SECTION_IDS.SERVICIOS_EXTERNOS) {
        return { policy: PricePolicy.EXT_COST, tier: null, matchedSection: true };
    }
    // Pharmacy sections
    if (id === qvet_catalog_1.QVET_SECTION_IDS.FARMACIA || id === qvet_catalog_1.QVET_SECTION_IDS.FARMACIA_INTERNA) {
        // Liquid items sold by ml
        if (saleUnit === 'ML') {
            return { policy: PricePolicy.COST_ML, tier: null, matchedSection: true }; // auto-tier
        }
        // Fractioned items (purchased in large packs, sold individually)
        if (conversionFactor > 4) {
            return { policy: PricePolicy.PHARM_FRAC, tier: PricingTier.HIGH_MARGIN, matchedSection: true };
        }
        // Original presentation
        return { policy: PricePolicy.PHARM, tier: PricingTier.HIGH_MARGIN, matchedSection: true };
    }
    // Medical supplies & other articles (cost-based with auto-tier)
    if (id === qvet_catalog_1.QVET_SECTION_IDS.INSUMOS_MEDICOS || id === qvet_catalog_1.QVET_SECTION_IDS.OTROS_ARTICULOS) {
        return { policy: PricePolicy.COST, tier: null, matchedSection: true };
    }
    // Unreachable today: every section in PRICED_SECTION_IDS is handled above. Kept so that
    // adding an id to that list without adding its rule fails safe instead of falling into
    // whichever branch happens to be last.
    return { policy: PricePolicy.MANUAL, tier: null, matchedSection: true };
}
// =============================================================================
// ROUNDING HELPER
// =============================================================================
/** Round to nearest multiple of 5 (minimum 5 for positive values) */
function roundToNearest5(value) {
    if (value <= 0)
        return 0;
    return Math.max(5, Math.round(value / 5) * 5);
}
/**
 * Calculate the recommended price PVP (con IVA) for a catalog item.
 *
 * Formula: unitPurchasePriceBI * markupFactor * (1 + vatSalePercent / 100)
 *
 * @returns The recommended price, or null if the policy has no formula
 */
function calculateRecommendedPricePVP(params) {
    const { pricePolicy, pricingTier, unitPurchasePriceBI, vatSalePercent } = params;
    const factor = getMarkupFactor(pricePolicy, pricingTier);
    if (factor === null)
        return null;
    if (unitPurchasePriceBI <= 0)
        return null;
    const priceBI = unitPurchasePriceBI * factor;
    const pricePVP = priceBI * (1 + vatSalePercent / 100);
    return roundToNearest5(pricePVP);
}
// =============================================================================
// FLOOR PRICE CALCULATION
// =============================================================================
/** Cost-based multiplier for floor price (PHARM, PHARM_FRAC, COST_ML, COST) — cost + 25% margin */
exports.MIN_PRICE_COST_FACTOR = 1.25;
/** Cost-based multiplier for floor price (EXT_COST) */
exports.MIN_PRICE_EXT_COST_FACTOR = 1.0;
/** Fraction of salePricePVP for recommended minimum price (COMP_SVC) */
exports.MIN_PRICE_COMP_SVC_SALE_FRACTION = 0.50;
/** Policies that use cost-based floor price (cost × factor × (1+VAT)) */
const COST_BASED_FLOOR_POLICIES = [
    PricePolicy.PHARM,
    PricePolicy.PHARM_FRAC,
    PricePolicy.COST_ML,
    PricePolicy.COST,
];
/**
 * Calculate floor prices (recommended minimum price and absolute floor) for a catalog item.
 *
 * Business rules by PricePolicy:
 * - PHARM, PHARM_FRAC, COST_ML, COST: both = unitCostBI × 0.25 × (1 + VAT/100), round5
 * - EXT_COST: both = unitCostBI × 1.0 × (1 + VAT/100), round5
 * - COMP_SVC: recommendedMinimumPrice = salePricePVP × 0.50 (round5), floorPrice = 0
 * - NONE, SPECIAL, MANUAL: both null
 */
function calculateFloorPrices(params) {
    const { pricePolicy, unitPurchasePriceBI, vatSalePercent, salePricePVP } = params;
    // Cost-based policies (PHARM, PHARM_FRAC, COST_ML, COST)
    if (COST_BASED_FLOOR_POLICIES.includes(pricePolicy)) {
        if (unitPurchasePriceBI <= 0)
            return { recommendedMinimumPrice: null, floorPrice: null };
        const price = roundToNearest5(unitPurchasePriceBI * exports.MIN_PRICE_COST_FACTOR * (1 + vatSalePercent / 100));
        return { recommendedMinimumPrice: price, floorPrice: price };
    }
    // External cost services
    if (pricePolicy === PricePolicy.EXT_COST) {
        if (unitPurchasePriceBI <= 0)
            return { recommendedMinimumPrice: null, floorPrice: null };
        const price = roundToNearest5(unitPurchasePriceBI * exports.MIN_PRICE_EXT_COST_FACTOR * (1 + vatSalePercent / 100));
        return { recommendedMinimumPrice: price, floorPrice: price };
    }
    // Internal services (market-study pricing)
    if (pricePolicy === PricePolicy.COMP_SVC) {
        const minPrice = salePricePVP > 0
            ? roundToNearest5(salePricePVP * exports.MIN_PRICE_COMP_SVC_SALE_FRACTION)
            : null;
        return { recommendedMinimumPrice: minPrice, floorPrice: 0 };
    }
    // NONE, SPECIAL, MANUAL — no floor prices
    return { recommendedMinimumPrice: null, floorPrice: null };
}
